import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { API_URL } from "../config.js";
import LoadingSpinner from "../components/LoadingSpinner";

const PerformanceComparisonChart = ({ subscriberid, serviceid }) => {
  const [selectedPeriod, setSelectedPeriod] = useState("MAX");
  const [activeTab, setActiveTab] = useState("chart");
  const [investmentHistory, setInvestmentHistory] = useState([]);
  const [investmentAttempts, setInvestmentAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [dataFetched, setDataFetched] = useState({
    history: false,
    attempts: false,
    chart: false
  });

  // 🧭 Pagination States for Attempts
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10; // records per page

  // Chart Data
  const defaultData = [
    { name: "Oct", year: "2022", growth: 100, equity: 100 },
    { name: "2023", year: "2023", growth: 108, equity: 106 },
    { name: "Apr", year: "2023", growth: 112, equity: 110 },
    { name: "Jul", year: "2023", growth: 120, equity: 118 },
    { name: "Oct", year: "2023", growth: 135, equity: 132 },
    { name: "2024", year: "2024", growth: 152, equity: 148 },
    { name: "Apr", year: "2024", growth: 172, equity: 168 },
    { name: "Jul", year: "2024", growth: 162, equity: 158 },
    { name: "Oct", year: "2024", growth: 195, equity: 188 },
    { name: "2025", year: "2025", growth: 220, equity: 208 },
    { name: "Apr", year: "2025", growth: 210, equity: 202 },
    { name: "Jul", year: "2025", growth: 208, equity: 189 },
  ];
  const [liveChartData, setLiveChartData] = useState(defaultData);

  const periodButtons = ["1M", "1Y", "3Y", "5Y", "MAX", "SIP"];

  // Fetch investment history - load immediately when component mounts
  const fetchInvestmentHistory = async () => {
    if (!subscriberid || !serviceid || dataFetched.history) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}subscription/investment-history`,
        {
          params: { subscriberid, serviceid },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setInvestmentHistory(response.data.data || []);
      setDataFetched(prev => ({ ...prev, history: true }));
    } catch (err) {
      console.error("Error fetching investment history:", err);
      setInvestmentHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch investment attempts - load immediately when component mounts
  const fetchInvestmentAttempts = async () => {
    if (!subscriberid || dataFetched.attempts) return;

    setLoadingAttempts(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}subscription/attempt-list`,
        {
          params: { subscriberid },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setInvestmentAttempts(response.data.data || []);
      setDataFetched(prev => ({ ...prev, attempts: true }));
    } catch (err) {
      console.error("Error fetching investment attempts:", err);
      setInvestmentAttempts([]);
    } finally {
      setLoadingAttempts(false);
    }
  };

  // Fetch portfolio chart data
  const fetchPortfolioChart = async () => {
    if (!serviceid || dataFetched.chart) return;

    setLoadingChart(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}appApi/service/portfolio/chart`,
        { serviceId: serviceid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(response.data.data);
      const fetchedData = response.data.data;
      setChartData(fetchedData);
      
      if (fetchedData?.livePerformance?.length > 0) {
        const smallCap = fetchedData.livePerformance.find(a => a.asset_name.includes('Small Cap') || a.asset_name.includes('Smallcap'));
        setSelectedAssetId(smallCap ? smallCap.asset_id : fetchedData.livePerformance[0].asset_id);
      }

      if (fetchedData?.availableDurations?.durations?.length > 0) {
        const durations = fetchedData.availableDurations.durations;
        const isValid = durations.some(d => d.key === selectedPeriod);
        if (!isValid) {
          if (durations.some(d => d.key === 'MAX')) {
            setSelectedPeriod('MAX');
          } else if (durations.some(d => d.key === '1Y')) {
            setSelectedPeriod('1Y');
          } else {
            setSelectedPeriod(durations[0].key);
          }
        }
      }
      
      setDataFetched(prev => ({ ...prev, chart: true }));
    } catch (err) {
      console.error("Error fetching portfolio chart:", err);
      setChartData(null);
    } finally {
      setLoadingChart(false);
    }
  };

  // Fetch live performance chart data based on period and asset
  const fetchLiveChartData = async () => {
    if (!serviceid || !selectedAssetId) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}appApi/service/live-performance/${serviceid}`,
        {},
        {
          params: {
            duration: selectedPeriod,
            assetId: selectedAssetId,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.data?.status === 'success' && response.data?.data?.items?.length > 0) {
        const items = response.data.data.items;
        // Sort items chronologically
        const sortedItems = [...items].sort((a, b) => new Date(a.sdate) - new Date(b.sdate));
        
        const formattedData = sortedItems.map((item) => {
          const d = new Date(item.sdate);
          const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          return {
            name: `${monthNames[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`,
            year: d.getFullYear().toString(),
            growth: parseFloat(item.portfolio_asset_amount || item.portfolio_amount) || 0,
            equity: parseFloat(item.asset_amount) || 0,
          };
        });
        setLiveChartData(formattedData);
      }
    } catch (err) {
      console.error("Error fetching live chart data:", err);
    }
  };

  useEffect(() => {
    fetchLiveChartData();
  }, [serviceid, selectedPeriod, selectedAssetId]);

  // Fetch all data when component mounts or when IDs change
  useEffect(() => {
    if (subscriberid && serviceid) {
      fetchInvestmentHistory();
      fetchInvestmentAttempts();
      fetchPortfolioChart();
    }
  }, [subscriberid, serviceid]);

  // Also fetch when tab changes (as backup)
  useEffect(() => {
    if (activeTab === "chart" && !dataFetched.chart && serviceid) {
      fetchPortfolioChart();
    }
    if (activeTab === "history" && !dataFetched.history && subscriberid && serviceid) {
      fetchInvestmentHistory();
    }
    if (activeTab === "attempts" && !dataFetched.attempts && subscriberid) {
      fetchInvestmentAttempts();
    }
  }, [activeTab, subscriberid, serviceid]);

  // 🧮 Pagination Calculation
  useEffect(() => {
    const totalRecords = investmentAttempts.length;
    setTotal(totalRecords);
    setPageCount(Math.ceil(totalRecords / limit));
    setPage(1);
  }, [investmentAttempts]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedAttempts = investmentAttempts.slice(start - 1, end);

  // Calculate totals for investment history
  const totals = investmentHistory.reduce(
    (acc, item) => {
      acc.quantity += item.quantity;
      acc.total += item.total;
      return acc;
    },
    { quantity: 0, total: 0 }
  );

  // Helper functions
  const getOrderTypeText = (ordertype) =>
    ordertype === 0 ? "Invest" : "Renewable";
  const getStatusText = (status) => (status === 0 ? "Failed" : "Success");
  const getStatusBadgeClass = (status) =>
    status === 0 ? "badge bg-danger" : "badge bg-success";
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  return (
    <div className="panel panel-default with-rounded-corner with-shadow p-10">
      {/* Tabs */}
      <ul className="nav nav-tabs" id="nav-tabs">
        <li className="nav-item">
          <span
            className={`nav-link ${activeTab === "chart" ? "active" : ""}`}
            onClick={() => setActiveTab("chart")}
            style={{
              cursor: "pointer",
              color: activeTab === "chart" ? "#007bff" : "#000",
            }}
          >
            Chart
          </span>
        </li>
        {investmentHistory.length > 0 && (
          <li className="nav-item">
            <span
              className={`nav-link ${activeTab === "history" ? "active" : ""}`}
              onClick={() => setActiveTab("history")}
              style={{
                cursor: "pointer",
                color: activeTab === "history" ? "#007bff" : "#000",
              }}
            >
              Investment History
            </span>
          </li>
        )}

        <li className="nav-item">
          <span
            className={`nav-link ${activeTab === "attempts" ? "active" : ""}`}
            onClick={() => setActiveTab("attempts")}
            style={{
              cursor: "pointer",
              color: activeTab === "attempts" ? "#007bff" : "#000",
            }}
          >
            Investment Attempts
          </span>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content" style={{ padding: "15px" }}>
        {/* Chart Tab */}
        {activeTab === "chart" && (
          <>
            {/* Header & Period Filters */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px 0",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "500",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {chartData?.service?.service_name || "Live Performance"} vs{" "}
                <div style={{ position: "relative", marginLeft: "5px" }}>
                  <select
                    value={selectedAssetId}
                    onChange={(e) => setSelectedAssetId(Number(e.target.value))}
                    style={{
                      color: "#007bff",
                      border: "none",
                      outline: "none",
                      appearance: "none",
                      cursor: "pointer",
                      background: "transparent",
                      fontWeight: "inherit",
                      fontSize: "18px",
                      fontFamily: "inherit",
                      paddingRight: "15px",
                    }}
                  >
                    {chartData?.livePerformance?.map((asset) => (
                      <option key={asset.asset_id} value={asset.asset_id}>
                        {asset.asset_name}
                      </option>
                    ))}
                    {!chartData?.livePerformance && (
                      <option value="">Equity Smallcap</option>
                    )}
                  </select>
                  <span
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#007bff",
                      pointerEvents: "none",
                      fontSize: "14px",
                    }}
                  >
                    ▼
                  </span>
                </div>
              </h3>

              <div
                style={{
                  display: "flex",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  padding: "3px",
                }}
              >
                {(chartData?.availableDurations?.durations || []).map((durationObj) => (
                  <button
                    key={durationObj.key}
                    onClick={() => setSelectedPeriod(durationObj.key)}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      fontWeight: "500",
                      borderRadius: "4px",
                      border: "none",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      backgroundColor:
                        selectedPeriod === durationObj.key ? "#007bff" : "transparent",
                      color: selectedPeriod === durationObj.key ? "white" : "#666",
                      minWidth: "35px",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedPeriod !== durationObj.key)
                        e.target.style.backgroundColor = "#e9ecef";
                    }}
                    onMouseLeave={(e) => {
                      if (selectedPeriod !== durationObj.key)
                        e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    {durationObj.key}
                  </button>
                ))}
              </div>
            </div>

            {/* Investment Summary */}
            <div style={{ marginBottom: "15px" }}>
              <div style={{ fontSize: "14px", color: "#666" }}>
                Current value of ₹
                <span style={{ fontWeight: "600", color: "#333" }}>100</span>{" "}
                invested once
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "12px",
                }}
              >
                {(() => {
                  switch (selectedPeriod) {
                    case '1M': return '1 month ago would be';
                    case '6M': return '6 months ago would be';
                    case '1Y': return '1 year ago would be';
                    case '3Y': return '3 years ago would be';
                    case '5Y': return '5 years ago would be';
                    case 'MAX': return 'since inception would be';
                    case 'SIP': return 'via SIP would be';
                    default: return `${selectedPeriod} ago would be`;
                  }
                })()}
              </div>

              {/* <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "25px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: "#007bff",
                    }}
                  ></div>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    {chartData?.service?.service_name || "Live Performance"}
                  </span>
                  <span style={{ fontWeight: "600", color: "#333" }}>
                    ₹ {liveChartData?.length > 0 ? liveChartData[liveChartData.length - 1].growth.toFixed(2) : "207.77"}
                  </span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: "#fd7e14",
                    }}
                  ></div>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    {chartData?.livePerformance?.find(a => a.asset_id === selectedAssetId)?.asset_name || "Equity Smallcap"}
                  </span>
                  <span style={{ fontWeight: "600", color: "#333" }}>
                    ₹ {liveChartData?.length > 0 ? liveChartData[liveChartData.length - 1].equity.toFixed(2) : "188.91"}
                   </span>
                </div>
              </div> */}
            </div>

            {/* Chart */}
            <div style={{ width: "100%", height: "280px" }}>
              <ResponsiveContainer>
                <LineChart
                  data={liveChartData}
                  margin={{ top: 20, right: 20, bottom: 20, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "#666" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#666" }}
                    tickLine={false}
                    axisLine={false}
                    domain={[100, 220]}
                    ticks={[100, 120, 140, 160, 180, 200, 220]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "12px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="growth"
                    stroke="#007bff"
                    strokeWidth={2.5}
                    dot={false}
                    name={chartData?.service?.service_name || "Live Performance"}
                  />
                  <Line
                    type="monotone"
                    dataKey="equity"
                    stroke="#fd7e14"
                    strokeWidth={2.5}
                    dot={false}
                    name={chartData?.livePerformance?.find(a => a.asset_id === selectedAssetId)?.asset_name || "Equity Smallcap"}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ padding: "10px 0", borderTop: "1px solid #f0f0f0" }}>
              <p
                style={{
                  fontSize: "12px",
                  color: "#888",
                  margin: 0,
                  lineHeight: "1.4",
                }}
              >
                <span style={{ fontWeight: "500" }}>Note:</span> Live
                performance includes rebalances. It is a tool to communicate
                factual & verifiable returns on behalf of the creator. It should
                not be considered as an advertisement, promotion or claim.
              </p>
            </div>
          </>
        )}

        {/* Investment History Tab */}
        {activeTab === "history" && (
          <>
            {loading && !dataFetched.history ? (
              <LoadingSpinner />
            ) : (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Avg Price</th>
                    <th>Quantity</th>
                    <th>Invested</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {investmentHistory.length > 0 ? (
                    investmentHistory.map((item) => (
                      <tr key={item.id}>
                        <td>{item.symbol}</td>
                        <td>{item.avgprice}</td>
                        <td>{item.quantity}</td>
                        <td>{item.total.toFixed(2)}</td>
                        <td>
                          {(() => {
                            const d = new Date(item.date);
                            const day = d.getDate();
                            const month = d.getMonth() + 1;
                            const year = d.getFullYear();
                            return `${day}-${month}-${year}`;
                          })()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No investment history found
                      </td>
                    </tr>
                  )}
                  {/* Totals row */}
                  {investmentHistory.length > 0 && (
                    <tr style={{ fontWeight: "600", backgroundColor: "#f8f9fa" }}>
                      <td>Total</td>
                      <td>-</td>
                      <td>{totals.quantity}</td>
                      <td>{totals.total.toFixed(2)}</td>
                      <td>-</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* Investment Attempts Tab */}
        {activeTab === "attempts" && (
          <>
            {loadingAttempts && !dataFetched.attempts ? (
              <LoadingSpinner />
            ) : (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order Type</th>
                    <th>Status</th>
                    <th>Created On</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAttempts.length > 0 ? (
                    paginatedAttempts.map((attempt, index) => (
                      <tr key={attempt.id}>
                        <td>{(page - 1) * limit + index + 1}</td>
                        <td>{getOrderTypeText(attempt.ordertype)}</td>
                        <td>
                          <span className={getStatusBadgeClass(attempt.status)}>
                            {getStatusText(attempt.status)}
                          </span>
                        </td>
                        <td>{formatDate(attempt.created_on)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No investment attempts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            {total > limit && (
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small>
                    Showing {start} to {end} of {total}
                  </small>
                </div>
                <div>
                  <button
                    className="btn btn-default btn-sm me-2"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    &lt;
                  </button>
                  <button
                    className="btn btn-default btn-sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === pageCount}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceComparisonChart;